/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useContext, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Avatar, Button, Flex, Modal, Tag, Tooltip } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import OrganizationContext from '@totem/components/orgManagement/orgContext';
import OrgDetailsTabs from '@totem/components/orgManagement/orgDetails/orgDetailsTabs';
import { UserDragItem } from '@totem/components/orgManagement/orgDetails/users/types';
import { Organization } from '@totem/components/orgManagement/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { DeviceQueryResult } from '@totem/types/devices';
import { isNotNull } from '@totem/utilities/common';
import { ORGANIZATION_ENDPOINT } from '@totem/utilities/endpoints';
import logger from '@totem/utilities/logger';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { stringifyArray } from '@totem/utilities/tableFilterUtilities';
const styles = {
  button: {
    marginRight: '4px',
  },
};

const OrgTable = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const {
    filteredData,
    loading,
    onAction,
    input,
    setInput,
    onUpdateNetSuiteKey,
    actionItem,
  } = useContext(OrganizationContext);
  const [isSending, setIsSending] = useState<boolean>(false);
  const DND_ITEM_TYPE = 'NETSUITE_CUSTOMER';
  const INTERNAL_USER_DND_ITEM_TYPE = 'INTERNAL_USER_ORGANIZATION';

  type DragItem = {
    netSuiteCustomerKey?: string;
    userOrg?: UserDragItem;
  };

  type DropRowProps = React.HTMLAttributes<HTMLTableRowElement> & {
    index?: number;
    record?: Organization;
  };

  const sendUpdateOrganizationType = useCallback(
    async (org: Organization, updateType: string) => {
      if (isSending) {
        return;
      }
      setIsSending(true);
      const method = updateType === 'DEACTIVATED' ? 'DELETE' : 'PUT';
      fetch(`${ORGANIZATION_ENDPOINT}/${org.id}`, {
        method,
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      }).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        onAction('refresh', null);
      });
    },
    [],
  );

  const sendAddOrganizationToUser = useCallback(
    async (organization: Organization, userOrg: UserDragItem) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      userOrg.organization.id = organization.id;

      userOrg.organization.name = organization.name;

      fetch(
        `${ORGANIZATION_ENDPOINT}/${userOrg.organization.id}/users/${userOrg.user.id}`,
        {
          method: 'PUT',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(userOrg.organization),
        },
      ).then((res) => {
        CheckResponseShowError(res);

        setIsSending(false);

        onAction('refresh', null);
      });
    },
    [],
  );

  const handleOrganiztionTypeChange = (
    selectedOrg: Organization,
    newType: string,
  ) => {
    if (newType === 'DEACTIVATED') {
      Modal.confirm({
        title: 'Are you sure?',
        content: `Please confirm that you would like to deactivate the organization "${selectedOrg.name}".  This will disable all user's access to this portal.`,
        okText: 'Deactivate',
        okType: 'danger',
        cancelText: 'Cancel',
        open: false,
        onOk: () => sendUpdateOrganizationType(selectedOrg, newType),
      });
    } else {
      sendUpdateOrganizationType(selectedOrg, newType);
    }
  };

  const handleInternalUserOnDrop = (
    organization: Organization,
    userOrg: UserDragItem,
  ) => {
    logger.debug(
      'handleInternalUserOnDrop: Dropped ',
      userOrg.user.email,
      ' (',
      userOrg.organization.name,
      ') ',
      ' on ',
      organization.name,
    );

    sendAddOrganizationToUser(organization, userOrg);
  };

  const handleNetSuiteCustomerOnDrop = (
    organizationId: string,
    netSuiteCustomerKey: string,
  ) => {
    logger.debug(
      'handleNetSuiteCustomerOnDrop: Dropped ',
      netSuiteCustomerKey,
      ' on ',
      organizationId,
    );

    onUpdateNetSuiteKey('ADD', organizationId, netSuiteCustomerKey);
  };

  const DroppableOrgRow = ({
    index,
    record,
    className,
    style,
    ...restProps
  }: DropRowProps) => {
    const ref = useRef<HTMLTableRowElement>(null);

    const [{ isOver, canDrop }, drop] = useDrop<
      DragItem,
      void,
      { isOver: boolean; canDrop: boolean }
    >({
      accept: [DND_ITEM_TYPE, INTERNAL_USER_DND_ITEM_TYPE],
      canDrop: (item) =>
        !!record?.id &&
        (!!item?.netSuiteCustomerKey || !!item?.userOrg?.user?.id),
      drop: (item) => {
        if (!record?.id) {
          return;
        }
        if (item?.netSuiteCustomerKey) {
          handleNetSuiteCustomerOnDrop(record.id, item.netSuiteCustomerKey);
          return;
        }
        if (item?.userOrg) {
          handleInternalUserOnDrop(record, item.userOrg);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    });

    drop(ref);

    return (
      <tr
        {...restProps}
        ref={ref}
        className={className}
        style={{
          backgroundColor: isOver && canDrop ? '#f0f5ff' : undefined,
          ...style,
        }}
        data-row-index={index}
      />
    );
  };

  const avatarColors = [
    '#1677ff',
    '#52c41a',
    '#faad14',
    '#f5222d',
    '#722ed1',
    '#13c2c2',
    '#eb2f96',
    '#fa8c16',
  ];
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return parts.length === 1
      ? parts[0][0].toUpperCase()
      : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  const getAvatarColor = (name: string) => {
    const hash = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return avatarColors[hash % avatarColors.length];
  };

  const columns: ColumnProps<Organization>[] = [
    {
      title: 'Organization',
      dataIndex: 'name',
      key: 'name',
      render: (_, record: Organization) => <span>{record.name}</span>,
      defaultSortOrder: 'ascend',
      sorter: (compA, compB) =>
        compA.name === compB.name ? 0 : compA.name > compB.name ? 1 : -1,
    },
    {
      title: 'Account Manager',
      dataIndex: 'accountManager',
      key: 'accountManager',
      render: (_, record: Organization) => (
        <div>
          {isNotNull(record.pointsOfContact) &&
            record.pointsOfContact
              .filter((chk) => chk.labels.includes('Account Manager'))
              .map((item) => (
                <Flex key={item.id} justify="flex-start" align="center">
                  <div style={{ marginRight: '16px' }}>
                    <Avatar
                      style={{ backgroundColor: getAvatarColor(item.name) }}
                    >
                      {getInitials(item.name)}
                    </Avatar>
                  </div>
                  <Flex vertical justify="flex-start" align="flex-start">
                    <Flex justify="flex-start" align="center">
                      {item.name}
                    </Flex>
                    <Flex justify="flex-start" align="center">
                      {item.email}
                    </Flex>
                  </Flex>
                </Flex>
              ))}
        </div>
      ),
    },
    {
      title: 'Services',
      dataIndex: 'services',
      key: 'services',
      render: (_, record: Organization) => (
        <span>
          {isNotNull(record.services) &&
            record.services.length > 0 &&
            record.services.map((val) => (
              <span key={val.id}>
                <Tag
                  style={{ marginLeft: '4px', marginBottom: '4px' }}
                  color={
                    val.status === 'Active'
                      ? 'success'
                      : val.status === 'DEACTIVATED'
                        ? 'error'
                        : 'default'
                  }
                  variant={'outlined'}
                >
                  ● {val.displayName}
                </Tag>{' '}
              </span>
            ))}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'type',
      key: 'status',
      render: (_, record: Organization) => (
        <Tag
          color={record.type === 'DEACTIVATED' ? 'error' : 'success'}
          variant={'outlined'}
        >
          {record.type === 'DEACTIVATED' ? 'Inactive' : 'Active'}
        </Tag>
      ),
      filterMultiple: false,
      filteredValue: stringifyArray(input.status),
      filters: [
        {
          text: 'Active',
          value: 'Active',
        },
        {
          text: 'Deactivated',
          value: 'Deactivated',
        },
      ],
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, record: Organization) => (
        <div className="center-table-cell">
          {permissions.internalSystemAdministrator.hasPermissions(
            userProfile,
          ) && (
            <>
              {record.type === 'DEACTIVATED' && (
                <IBTooltip
                  content={permissions.internalSystemAdministrator.tooltip}
                >
                  <Tooltip title="Reactivate" placement="top">
                    <Button
                      shape="circle"
                      icon={<PlusCircleOutlined />}
                      style={styles.button}
                      onClick={() =>
                        handleOrganiztionTypeChange(record, 'CUSTOMER_OWNED')
                      }
                    />
                  </Tooltip>
                </IBTooltip>
              )}
              {record.type !== 'DEACTIVATED' && (
                <IBTooltip
                  content={permissions.internalSystemAdministrator.tooltip}
                >
                  <Tooltip title="Deactivate" placement="top">
                    <Button
                      shape="circle"
                      icon={<MinusCircleOutlined />}
                      style={styles.button}
                      onClick={() =>
                        handleOrganiztionTypeChange(record, 'DEACTIVATED')
                      }
                    />
                  </Tooltip>
                </IBTooltip>
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<DeviceQueryResult>,
    sorter,
  ) => {
    const { ...params } = filters;
    // @ts-ignore
    setInput({
      ...input,
      ...params,
    });
  };

  const handleExpandRow = (expanded: boolean, record: Organization) => {
    if (expanded) {
      onAction('expanded', record);
    } else {
      onAction('expanded', null);
    }
  };

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey={(record) => record.id}
        onChange={handleTableChange}
        components={{
          body: {
            row: DroppableOrgRow,
          },
        }}
        onRow={(record, index) =>
          ({
            index,
            record,
          }) as React.HTMLAttributes<HTMLElement>
        }
        pagination={{
          showSizeChanger: true,
        }}
        expandable={{
          expandedRowRender: (record) => (
            <OrgDetailsTabs organization={record} />
          ),
          defaultExpandAllRows: false,
          expandedRowKeys: isNotNull(actionItem) ? [actionItem.id] : [],
          onExpand: handleExpandRow,
        }}
      />
    </>
  );
};
export default OrgTable;
